import React from 'react';
import {List} from 'immutable';
import styled, {css} from 'styled-components';
import {useLocation} from 'react-router-dom';

import {Badge, type BadgeProps} from '@famly/mf_data-display_badge';
import {Stack} from '@famly/mf_layout_stack';
import Link, {type LinkProps} from 'web-app/react/components/link/link';
import {Body} from 'web-app/react/components/text/text';
import {Flex} from 'web-app/react/components/layout/layout';
import Icon from 'web-app/react/components/icons/legacy/icon';
import ExpandableButton from 'web-app/react/components/expandable-button/expandable-button';
import {s0, s2, type SingleSpacingDescription} from 'web-app/styleguide/spacing';
import {hasValue, type InferProps} from 'web-app/util/typescript';
import * as IntercomConstants from 'web-app/services/intercom-constants';
import {isNonEmpty, type NonEmptyArray} from 'web-app/util/non-empty-array';
import {TARGET_IDS} from 'web-app/services/intercom-constants';

export const TabsContainer = styled(({equalSpacing, ...rest}) => <Flex {...rest} />)<
    InferProps<typeof Flex> & {equalSpacing?: boolean}
>`
    ${props =>
        props.theme.containerConfiguration.tabSeparatorBackground
            ? css`
                  background: ${props.theme.containerConfiguration.tabSeparatorBackground};
              `
            : ''}

    display: flex;
    ${props =>
        props.equalSpacing
            ? css`
                  justify-content: space-around;
              `
            : ''}
`;

export const TabTitle = styled(Body)`
    text-align: center;
    &:after {
        display: block;
        content: attr(title);
        ${props => props.theme.fontConfiguration.emphasized};
        height: 0;
        overflow: hidden;
        visibility: hidden;
    }
`;

interface TabProps {
    selected: boolean;
    children: any;
    css?: ReturnType<typeof css>;
}

const StyledTabCSS = css<TabProps>`
    white-space: nowrap;
    flex-shrink: 0;
    cursor: pointer;
    position: relative;
    padding: 0 16px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
    transition: background-color 0.1s;
    &:first-child {
        margin-left: -16px;
    }
    &:last-child {
        margin-right: -16px;
    }
    img {
        height: 20px;
        width: 20px;
        border-radius: 20px;
    }
    &:after {
        content: '';
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 0;
        height: 4px;
        border-radius: 2px;
        background-color: ${props => props.theme.active};
        opacity: 0;
        pointer-events: none;
    }
    ${props =>
        props.selected
            ? css`
                  &:after {
                      opacity: 1;
                  }
              `
            : css`
                  ${TabTitle} {
                      color: ${props.theme.textSecondary};
                  }

                  &:hover {
                      background-color: ${props.theme.backgroundHover};
                  }
              `}

    ${props => (props.css ? props.css : '')}
    &:focus {
        outline: none;
    }
`;

export const StyledTab = styled.div<TabProps>`
    ${StyledTabCSS}
`;
const StyledTabLink = styled(({...rest}) => <Link {...rest} />)<TabProps & LinkProps>`
    ${StyledTabCSS}
`;

const StyledTabElement = styled(Flex).attrs(() => ({
    align: 'center',
    justify: 'center',
}))``;

export interface BaseTabProps {
    id: string;
    ['data-e2e-id']?: string;
    ['data-intercom-target']?: string;
    title?: string;
    icon?: string | object;
    className?: string;
    tabs?: undefined;
    afterElement?: React.ReactNode;
    tabCss?: ReturnType<typeof css>;
    badgeProps?: BadgeProps;
    onClick?: (id: string) => any;
}

/**
 * Tabs can be supplied in two ways:
 * 1. Handled via onClick (either on individual tab or onClick given to <Tabs/>)
 *     In this case "selected" will have to be controlled by consumer
 * 2. Leads to a route change. When this is the case we use react-router to determine
 *    if the tab is active or not.
 */
type TabWithRoute = BaseTabProps & {
    url: string;
    selected?: undefined;
};
type TabWithState = BaseTabProps & {
    selected: boolean;
    url?: undefined;
};
export type TabOwnProps = TabWithRoute | TabWithState;

const tabHasRoute = (tab: TabOwnProps): tab is TabWithRoute => 'url' in tab;

export interface TabList {
    title: string;
    id: string;
    tabs: List<TabOwnProps>;
    icon?: string | object;
}
export type InputTab = TabOwnProps | TabList;
const isTabList = (tab: InputTab): tab is TabList => List.isList(tab.tabs);

const TAB_MARGIN: [SingleSpacingDescription, SingleSpacingDescription] = [s0, s2];

const Tab: React.FC<React.PropsWithChildren<TabOwnProps>> = React.memo(props => {
    const {onClick, id} = props;

    // Pass in onClick from parent component (If onClick is passed to <Tabs/>)
    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(id);
        }
    }, [onClick, id]);

    // Type guarding
    if (tabHasRoute(props)) {
        return <TabWithRouteComponent {...props} onClick={handleClick} />;
    }
    return <TabRenderer {...props} onClick={handleClick} />;
});

/**
 * This component will use react-router to determine if the tab is active based on the current route.
 */
const TabWithRouteComponent: React.FC<
    React.PropsWithChildren<Omit<TabWithRoute, 'onClick'> & {onClick: VoidFunction}>
> = props => {
    const {url} = props;

    const {pathname: activePath} = useLocation();

    const selected = `#${activePath}` === url;

    return <TabRenderer {...props} selected={selected} href={url} />;
};

/**
 * This component simply produces either a Link or a DIV, with content, based on the props
 */
const TabRenderer: React.FC<
    React.PropsWithChildren<
        Omit<TabOwnProps, 'onClick' | 'url'> & {selected: boolean; onClick: VoidFunction; href?: string}
    >
> = props => {
    const content = (
        <>
            {props.icon ? (
                <StyledTabElement margin={TAB_MARGIN}>
                    <Icon icon={props.icon} />
                </StyledTabElement>
            ) : null}
            {props.title ? <TabTitle emphasized={props.selected}>{props.title}</TabTitle> : null}
            {props.afterElement ? <StyledTabElement margin={TAB_MARGIN}>{props.afterElement}</StyledTabElement> : null}
        </>
    );

    const contentWithBadge = props.badgeProps ? <Badge {...props.badgeProps}>{content}</Badge> : content;

    const tabProps = {
        selected: props.selected,
        className: props.className,
        onClick: props.onClick,
        css: props.tabCss,
        href: props.href,
        'data-e2e-id': hasValue(props['data-e2e-id']) ? props['data-e2e-id'] : `TAB-${props.id}`,
        'data-intercom-target': hasValue(props['data-intercom-target'])
            ? props['data-intercom-target']
            : `${IntercomConstants.TARGET_IDS.TAB_LINK}-${props.id}`,
        'data-is-active': props.selected,
    };

    if (hasValue(tabProps.href)) {
        return <StyledTabLink {...tabProps}>{contentWithBadge}</StyledTabLink>;
    }

    return <StyledTab {...tabProps}>{contentWithBadge}</StyledTab>;
};

export interface TabsOwnProps {
    tabs: List<InputTab>;
    onTabClicked?: TabOwnProps['onClick']; // Maybe remove?

    // Presentational props
    className?: string;
    equalWidth?: boolean; // Will make all tabs width "(100 / tabs.size)%"
    equalSpacing?: boolean; // Will apply "justify-content: space-around" to tabs
}

interface TabCSSProps {
    equalWidth?: TabsOwnProps['equalWidth'];
    count: number;
}

const TabCSS = (props: TabCSSProps) => css`
    ${props.equalWidth
        ? css`
              width: ${100 / props.count}%;
              box-sizing: border-box;
              padding-left: 4px;
              padding-right: 4px;
              &&& {
                  margin: 0;
              }
          `
        : ''}
`;

const Tabs: React.FC<React.PropsWithChildren<TabsOwnProps>> = ({
    tabs,
    onTabClicked,
    className,
    equalWidth,
    equalSpacing,
}) => {
    const tabCSS = TabCSS({count: tabs.size, equalWidth});
    return (
        <TabsContainer
            className={className}
            equalSpacing={equalSpacing}
            data-intercom-target={TARGET_IDS.CHILD_PROFILE_TABS_OVERVIEW}
        >
            {tabs.map(tab => {
                if (isTabList(tab)) {
                    return <TabListRenderer key={tab.id} tab={tab} onClick={onTabClicked} tabCSS={tabCSS} />;
                }
                return <Tab tabCss={tabCSS} key={tab.id} onClick={onTabClicked} {...tab} />;
            })}
        </TabsContainer>
    );
};

export default Tabs;

interface ListTabProps {
    tab: TabList;
    onClick: TabsOwnProps['onTabClicked'];
    tabCSS?: ReturnType<typeof css>;
}

/**
 * This component propogates the rendering of nested tabs.
 * 1. If the list is empty we render null
 * 2. If the list only contains a single tab, we render it as a normal tab
 * 3. If it contains more than one, we render it as a dropdown
 */
const TabListRenderer: React.FC<React.PropsWithChildren<ListTabProps>> = ({tab, onClick, tabCSS}) => {
    const {tabs, title, icon} = tab;

    const arrayOfTabs = React.useMemo(() => tabs.toArray(), [tabs]);

    if (!isNonEmpty(arrayOfTabs)) {
        return null;
    }

    if (arrayOfTabs.length === 1) {
        const [firstTab] = arrayOfTabs;
        return (
            <Tab
                tabCss={tabCSS}
                key={firstTab.id}
                {...firstTab}
                onClick={() => (firstTab.onClick ? firstTab.onClick(firstTab.id) : onClick && onClick(firstTab.id))}
            />
        );
    }
    return <TabList onClick={onClick} tabs={arrayOfTabs} title={title} icon={icon} tabCSS={tabCSS} />;
};

/**
 * This component takes in an array of tabs and renders them in a dropdown.
 * It attempts to compute if one of them is selected and then applies styling to the dropdown button.
 */
const TabList: React.FC<
    React.PropsWithChildren<
        {tabs: NonEmptyArray<TabOwnProps>} & Pick<ListTabProps, 'tabCSS' | 'onClick'> & Omit<TabList, 'tabs' | 'id'>
    >
> = ({tabs, onClick, tabCSS, icon, title}) => {
    const {pathname: activePath} = useLocation();

    /**
     * Compute parameters to determine if tab is active.
     * I've implemented it this way to only compute the routes once per mount.
     */
    const selectedParams = React.useMemo(
        () =>
            tabs.map(tab => {
                if (tabHasRoute(tab)) {
                    return tab.url;
                }
                return tab.selected;
            }),
        [tabs],
    );

    /**
     * Whenever location changes we iterate through the computed parameters and see if anything should update
     *
     * This operation is very cheap and doesn't need to be memo'd
     */
    const selected = selectedParams.some(param => {
        switch (typeof param) {
            case 'string':
                return param === `#${activePath}`;
            case 'boolean':
                return param;
            default:
                return false;
        }
    });

    const tabItems = React.useMemo(() => {
        return tabs.map(tab => {
            if (tabHasRoute(tab)) {
                return {
                    id: tab.id,
                    type: 'link',
                    onClick: () => (tab.onClick ? tab.onClick(tab.id) : onClick && onClick(tab.id)),
                    href: tab.url,
                    icon: tab.icon,
                    title: tab.title,
                };
            }
            return {
                id: tab.id,
                onClick: () => (tab.onClick ? tab.onClick(tab.id) : onClick && onClick(tab.id)),
                icon: tab.icon,
                title: tab.title,
            };
        });
    }, [onClick, tabs]);

    return (
        <StyledTab selected={selected} css={tabCSS}>
            <StyledListTabButton selected={selected} items={tabItems}>
                <Stack alignItems="center">
                    {icon ? (
                        <StyledTabElement margin={TAB_MARGIN}>
                            <Icon icon={icon} />
                        </StyledTabElement>
                    ) : null}
                    <ListTabTitle selected={selected}>{title}</ListTabTitle>
                </Stack>
            </StyledListTabButton>
        </StyledTab>
    );
};

const StyledExpandableButton = styled(ExpandableButton)`
    justify-content: center;
    height: 100%;
    width: 100%;
`;

const ListTabButton = styled(({className, ...rest}: any) => (
    <StyledExpandableButton
        btnClassName={className}
        {...rest}
        buttonSize={'mini'}
        appearance="none"
        canCollapse={true}
        showArrow={false}
    />
)).attrs(() => ({}))<any>`
    text-align: center;
    outline: none !important;
    g {
        fill: ${props => props.theme.textSecondary};
    }
`;

const StyledListTabButton = styled(ListTabButton)`
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    & > * {
        margin: 0 auto;
    }
`;

const ListTabTitle = styled(({...rest}) => <Body {...rest} />)<{selected: boolean}>`
    color: ${props => props.theme.textSecondary};
`;
